import { format, parseISO } from 'date-fns';
import {
  AnimatePresence,
  motion,
  type Transition,
  type Variants,
} from 'framer-motion';
import {
  AlertCircle,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useUpsertMeetingCandidateEmailMutation } from '@/modules/coordination/mutations/useUpsertMeetingCandidateEmailMutation';
import { useCoordinationMeetingQuery } from '@/modules/coordination/queries/useCoordinationMeetingQuery';
import { Button } from '@/modules/ui/components/button/Button';
import { Heading } from '@/modules/ui/components/heading/Heading';
import { Input } from '@/modules/ui/components/input/Input';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pageVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const pageTransition: Transition = { duration: 0.3, ease: 'easeOut' };

export const CandidateCoordinationMeeting = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const meetingQuery = useCoordinationMeetingQuery(meetingId);
  const upsertEmailMutation = useUpsertMeetingCandidateEmailMutation();
  const [draftEmail, setDraftEmail] = useState<null | string>(null);

  if (!meetingId || meetingQuery.isError) {
    return (
      <div className="flex min-h-screen flex-col bg-linear-to-br from-neutral-50 to-neutral-100">
        <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6 md:py-12 lg:px-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-destructive/10 mb-6 flex h-16 w-16 items-center justify-center rounded-full">
              <AlertCircle className="text-destructive h-8 w-8" />
            </div>

            <Heading variant="h3" className="mb-3">
              This link is no longer active
            </Heading>

            <Paragraph>
              This meeting link has expired or isn&apos;t valid. Contact the
              recruiter who sent it to get a new one.
            </Paragraph>
          </div>
        </main>
      </div>
    );
  }

  if (!meetingQuery.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-linear-to-br from-neutral-50 to-neutral-100">
        <Loader2 className="text-primary mb-4 h-8 w-8 animate-spin" />
        <Paragraph>Getting your meeting details...</Paragraph>
      </div>
    );
  }

  const { meeting, company, hiringManager, candidate } = meetingQuery.data;
  const storedEmail = candidate.email ?? '';
  const inputValue = draftEmail ?? storedEmail;
  const trimmedDraftEmail = inputValue.trim();
  const isEmailValid = EMAIL_PATTERN.test(trimmedDraftEmail);
  const isDirty = draftEmail !== null && trimmedDraftEmail !== storedEmail;
  const shouldShowValidationError = inputValue.length > 0 && !isEmailValid;
  const isSaveDisabled =
    upsertEmailMutation.isPending ||
    !isDirty ||
    !trimmedDraftEmail ||
    !isEmailValid;
  const showSavedConfirmation = !isDirty && !!storedEmail;

  const handleSave = () => {
    if (isSaveDisabled) {
      return;
    }

    upsertEmailMutation.mutate(
      {
        meetingId,
        body: { email: trimmedDraftEmail },
      },
      {
        onSuccess: () => {
          setDraftEmail(null);
          toast.success('Saved your email.');
        },
        onError: (error) => {
          toast.error(
            `Could not save your email: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen flex-col bg-linear-to-br from-neutral-50 to-neutral-100">
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center px-4 py-10 sm:px-6 md:py-16 lg:px-8">
        <AnimatePresence mode="wait">
          <motion.div
            key="coordination-meeting"
            variants={pageVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={pageTransition}
            className="mx-auto w-full"
          >
            <div className="mb-6">
              <Heading variant="h2" className="mb-2">
                Let&apos;s get your{' '}
                <span className="font-serif font-normal italic">
                  coordination meeting
                </span>{' '}
                set up.
              </Heading>
              <Paragraph className="text-muted-foreground">
                Here are the details for your upcoming meeting.
              </Paragraph>
            </div>

            <div className="bg-card mb-6 rounded-xl border p-5">
              <Subheading variant="h5" className="mb-3 flex items-center gap-2">
                <Users className="text-primary h-4 w-4 shrink-0" />
                Meeting with {hiringManager.name}
              </Subheading>

              <div className="text-muted-foreground flex flex-col gap-2 text-sm">
                <span className="flex items-center gap-1.5">
                  <Building2 className="text-primary h-4 w-4" />
                  {company.name}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="text-primary h-4 w-4" />
                  {format(
                    parseISO(meeting.startsAt),
                    "EEEE, MMMM d 'at' h:mm a",
                  )}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="text-primary h-4 w-4" />
                  {meeting.durationMinutes} minutes
                </span>
              </div>
            </div>

            <div className="bg-card rounded-xl border p-5">
              <Subheading variant="h5" className="mb-1">
                Where should we send the invite?
              </Subheading>
              <Paragraph className="text-muted-foreground mb-4 text-sm">
                We&apos;ll email your calendar invite here.
              </Paragraph>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  type="email"
                  value={inputValue}
                  onChange={(event) => setDraftEmail(event.target.value)}
                  placeholder="you@example.com"
                  disabled={upsertEmailMutation.isPending}
                  aria-invalid={shouldShowValidationError}
                  className="h-10 flex-1"
                />
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleSave}
                  loading={upsertEmailMutation.isPending}
                  disabled={isSaveDisabled}
                >
                  Save
                </Button>
              </div>

              {shouldShowValidationError ? (
                <p className="text-destructive mt-2 text-xs">
                  Enter a valid email address to save it.
                </p>
              ) : showSavedConfirmation ? (
                <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-xs">
                  <CheckCircle className="text-primary h-3.5 w-3.5" />
                  Saved: {storedEmail}
                </p>
              ) : null}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};
